import React, { useState } from 'react';
import { Volume2, VolumeX, Sparkles, Check } from 'lucide-react';
import { useDemoLang, speak, stopSpeak } from './demoLang';

const PERSONAS = [
  {
    id: 'newton', name: 'Isaac Newton', subject_fr: 'Physique & Mathématiques', subject_en: 'Physics & Math',
    quote_fr: 'Chaque problème cache un schéma. Trouvons-le ensemble.',
    quote_en: 'Every problem has a pattern. Let us find it together.',
    color: '#8b1f2f', emoji: '🍎', ttsLangFr: 'fr-FR', ttsLangEn: 'en-GB',
  },
  {
    id: 'curie', name: 'Marie Curie', subject_fr: 'Sciences & Chimie', subject_en: 'Science & Chemistry',
    quote_fr: 'La curiosité est ta plus grande expérience.',
    quote_en: 'Curiosity is your greatest experiment.',
    color: '#2d5f4f', emoji: '⚗️', ttsLangFr: 'fr-FR', ttsLangEn: 'en-US',
  },
  {
    id: 'socrates', name: 'Socrates', subject_fr: 'Philosophie & Pensée Critique', subject_en: 'Philosophy & Critical Thinking',
    quote_fr: 'Une question bien posée est à moitié répondue.',
    quote_en: 'A question well asked is half answered.',
    color: '#5b5b8f', emoji: '🏛️', ttsLangFr: 'fr-FR', ttsLangEn: 'en-US',
  },
  {
    id: 'mandela', name: 'Nelson Mandela', subject_fr: 'Histoire & Sciences Sociales', subject_en: 'History & Social Studies',
    quote_fr: "L'éducation est l'arme la plus puissante.",
    quote_en: 'Education is the most powerful weapon.',
    color: '#c47a2a', emoji: '🌍', ttsLangFr: 'fr-FR', ttsLangEn: 'en-US',
  },
  {
    id: 'ada', name: 'Ada Lovelace', subject_fr: 'Informatique & Logique', subject_en: 'Computer Science & Logic',
    quote_fr: "L'esprit est l'algorithme.",
    quote_en: 'The mind is the algorithm.',
    color: '#a24d8f', emoji: '💻', ttsLangFr: 'fr-FR', ttsLangEn: 'en-GB',
  },
  {
    id: 'coach', name: 'Your Coach', name_fr: 'Ton Coach', subject_fr: 'Toutes matières — énergie maximale', subject_en: 'All subjects — max energy',
    quote_fr: 'Tu es capable de plus que tu ne le crois. On y va !',
    quote_en: "You're capable of more than you know. Let's GO!",
    color: '#FFCC00', emoji: '⚡', ttsLangFr: 'fr-FR', ttsLangEn: 'en-US',
  },
];

export default function PersonaShowcase() {
  const { lang, t } = useDemoLang();
  const [selected, setSelected] = useState('newton');
  const [speaking, setSpeaking] = useState(null);

  const doSpeak = (p) => {
    if (speaking === p.id) { stopSpeak(); setSpeaking(null); return; }
    const text = lang === 'fr' ? p.quote_fr : p.quote_en;
    const ttsLang = lang === 'fr' ? p.ttsLangFr : p.ttsLangEn;
    speak(text, ttsLang);
    setSpeaking(p.id);
    setTimeout(() => setSpeaking((s) => (s === p.id ? null : s)), 6000);
  };

  const current = PERSONAS.find((p) => p.id === selected) || PERSONAS[0];

  return (
    <div className="space-y-5" data-testid="persona-showcase">
      {/* Featured selected persona */}
      <section className="rounded-3xl overflow-hidden shadow-squishyLg border-4" style={{ borderColor: current.color }} data-testid="persona-featured">
        <div className="p-6 text-white" style={{ background: `linear-gradient(135deg, ${current.color}, #0F4C4C)` }}>
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-full bg-white/20 border-4 border-white/40 flex items-center justify-center text-5xl shrink-0">{current.emoji}</div>
            <div className="min-w-0">
              <div className="text-white/80 font-heading font-bold text-[10px] uppercase tracking-wider">{t('Tuteur sélectionné', 'Selected tutor')}</div>
              <h2 className="font-heading text-2xl font-bold" data-testid="persona-current-name">{current.name_fr && lang === 'fr' ? current.name_fr : current.name}</h2>
              <div className="text-white/85 text-sm">{t(current.subject_fr, current.subject_en)}</div>
            </div>
          </div>
          <blockquote className="mt-4 text-white/95 italic text-sm leading-relaxed border-l-4 border-white/40 pl-3">
            "{lang === 'fr' ? current.quote_fr : current.quote_en}"
          </blockquote>
          <button onClick={() => doSpeak(current)}
            className="mt-4 inline-flex items-center gap-2 h-10 px-4 rounded-full bg-white/20 hover:bg-white/30 text-white font-heading font-bold text-sm transition-colors"
            data-testid="persona-speak">
            {speaking === current.id ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            {speaking === current.id ? t('Arrêter', 'Stop') : t('Écouter la citation', 'Hear the quote')}
          </button>
        </div>
      </section>

      <div>
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="w-4 h-4 text-navy" />
          <h3 className="font-heading font-bold text-navy">{t('Choisir un tuteur', 'Choose a tutor')}</h3>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="persona-grid">
          {PERSONAS.map((p) => {
            const active = selected === p.id;
            const displayName = p.name_fr && lang === 'fr' ? p.name_fr : p.name;
            return (
              <button key={p.id} onClick={() => setSelected(p.id)}
                className={`p-4 text-left rounded-2xl bg-white border-2 transition-all ${active ? 'shadow-squishyLg scale-[1.02]' : 'border-cream hover:border-teal/60'}`}
                style={active ? { borderColor: p.color } : {}}
                data-testid={`persona-card-${p.id}`}>
                <div className="flex items-start gap-2">
                  <div className="w-11 h-11 rounded-full flex items-center justify-center text-2xl shrink-0" style={{ background: `${p.color}20` }}>{p.emoji}</div>
                  {active && <div className="w-6 h-6 rounded-full flex items-center justify-center text-white ml-auto" style={{ background: p.color }}><Check className="w-3 h-3" /></div>}
                </div>
                <div className="font-heading font-bold text-navy text-sm mt-2 leading-tight">{displayName}</div>
                <div className="text-navy/60 text-[11px] mt-0.5 leading-tight">{t(p.subject_fr, p.subject_en)}</div>
              </button>
            );
          })}
        </div>
      </div>

      <p className="text-navy/50 text-xs text-center italic">
        {t('Astuce : appuie sur "Écouter la citation" pour entendre le tuteur (Web Speech API — aucun serveur externe).',
           'Tip: tap "Hear the quote" to hear the tutor speak (Web Speech API — no external server).')}
      </p>
    </div>
  );
}
